import { io } from 'socket.io-client';

class LogService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.logHandlers = new Set();
  }

  // 连接到远端日志服务器
  connect(serverUrl = 'ws://localhost:3001') {
    try {
      console.log('[LogService] 正在连接到日志服务器:', serverUrl);
      
      this.socket = io(serverUrl, {
        transports: ['websocket', 'polling'],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 10000,
        timeout: 50000,
      });

      this.setupEventListeners();
      
    } catch (error) {
      console.error('[LogService] 连接失败:', error);
    }
  }

  // 设置事件监听器
  setupEventListeners() {
    if (!this.socket) return;

    // 连接成功
    this.socket.on('connect', () => {
      console.log('[LogService] 已连接到远端日志服务器');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      // 注册接收日志消息
      this.socket.emit('subscribe-logs');
    });

    // 接收日志消息
    this.socket.on('log-message', (data) => {
      this.handleLogMessage(data);
    });

    // 接收字符串消息（备用事件名）
    this.socket.on('string-message', (message) => {
      this.handleStringMessage(message);
    });

    // 连接断开
    this.socket.on('disconnect', (reason) => {
      console.log('[LogService] 与日志服务器断开连接:', reason);
      this.isConnected = false;
    });

    // 连接错误
    this.socket.on('connect_error', (error) => {
      console.error('[LogService] 连接错误:', error);
      this.reconnectAttempts++;
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('[LogService] 达到最大重连次数，停止重连');
      }
    });

    // 重新连接
    this.socket.on('reconnect', (attemptNumber) => {
      console.log(`[LogService] 第 ${attemptNumber} 次重连成功`);
    });

    // 重连失败
    this.socket.on('reconnect_failed', () => {
      console.error('[LogService] 重连失败，请检查网络连接');
    });
  }

  // 处理日志消息
  handleLogMessage(data) {
    const timestamp = new Date().toLocaleString('zh-CN');
    let message;

    // 处理不同格式的数据
    if (typeof data === 'string') {
      message = data;
    } else if (data && typeof data === 'object') {
      if (data.message) {
        message = data.message;
      } else {
        message = JSON.stringify(data);
      }
    } else {
      message = String(data);
    }

    // 格式化并打印日志
    const formattedLog = `[${timestamp}] [远端日志] ${message}`;
    console.log(formattedLog);

    // 通知所有注册的处理器
    this.logHandlers.forEach(handler => {
      try {
        handler(formattedLog, data);
      } catch (error) {
        console.error('[LogService] 处理器执行错误:', error);
      }
    });
  }

  // 处理字符串消息
  handleStringMessage(message) {
    const timestamp = new Date().toLocaleString('zh-CN');
    const stringMessage = String(message);
    
    const formattedLog = `[${timestamp}] [远端字符串] ${stringMessage}`;
    console.log(formattedLog);

    // 通知所有注册的处理器
    this.logHandlers.forEach(handler => {
      try {
        handler(formattedLog, stringMessage);
      } catch (error) {
        console.error('[LogService] 字符串处理器执行错误:', error);
      }
    });
  }

  // 添加日志处理器
  addLogHandler(handler) {
    if (typeof handler === 'function') {
      this.logHandlers.add(handler);
      return () => this.logHandlers.delete(handler); // 返回清理函数
    }
  }

  // 移除日志处理器
  removeLogHandler(handler) {
    this.logHandlers.delete(handler);
  }

  // 发送日志到本地（仅本地处理，不发送到远端）
  sendLog(message, level = 'info') {
    const logData = {
      message: String(message),
      level,
      timestamp: new Date().toISOString(),
      source: 'frontend-local'
    };

    // 只在本地处理，格式化并显示
    const timestamp = new Date().toLocaleString('zh-CN');
    const formattedLog = `[${timestamp}] [本地日志] ${logData.message}`;
    console.log(formattedLog);

    // 通知所有注册的处理器，让日志显示在页面上
    this.logHandlers.forEach(handler => {
      try {
        handler(formattedLog, logData);
      } catch (error) {
        console.error('[LogService] 本地处理器执行错误:', error);
      }
    });
  }

  // 断开连接
  disconnect() {
    if (this.socket) {
      console.log('[LogService] 正在断开连接...');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  // 获取连接状态
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts
    };
  }
}

// 创建单例实例
const logService = new LogService();

export default logService; 